/** 관리자 화면 공용 UI 조각 */
import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { C, STATUS_COLOR, STATUS_LABEL } from '../common';
import type { ReportStatus } from '../api';

export const Card: React.FC<{ title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; sx?: any }> = ({ title, action, children, sx }) => (
  <Paper variant="outlined" sx={{ p: 2.25, borderColor: C.line, borderRadius: 3, ...sx }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography fontWeight={800} sx={{ flex: 1 }}>{title}</Typography>
        {action}
      </Box>
    )}
    {children}
  </Paper>
);

export const Kpi: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'warn' | 'ok' | 'err' | 'plain'; onClick?: () => void }> = ({ label, value, sub, tone = 'plain', onClick }) => {
  const color = tone === 'warn' ? C.warn : tone === 'ok' ? C.ok : tone === 'err' ? C.err : C.ink;
  return (
    <Paper variant="outlined" onClick={onClick} sx={{ p: 2, borderColor: C.line, borderRadius: 3, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: '#f5c400' } : undefined }}>
      <Typography sx={{ fontSize: 13, color: C.sub, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.3 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: C.sub }}>{sub}</Typography>}
    </Paper>
  );
};

export const StatusChip: React.FC<{ status: ReportStatus; small?: boolean }> = ({ status, small }) => (
  <Chip size="small" label={STATUS_LABEL[status]} sx={{ color: STATUS_COLOR[status].fg, bgcolor: STATUS_COLOR[status].bg, height: small ? 22 : 24 }} />
);

export const ToneChip: React.FC<{ label: React.ReactNode; tone: 'warn' | 'ok' | 'err' | 'info' | 'gray'; dashed?: boolean; sx?: any }> = ({ label, tone, dashed, sx }) => {
  const map = { warn: [C.warn, C.warnBg], ok: [C.ok, C.okBg], err: [C.err, C.errBg], info: [C.info, C.infoBg], gray: [C.gray, C.grayBg] } as const;
  const [fg, bg] = map[tone];
  return <Chip size="small" label={label} sx={{ color: fg, bgcolor: dashed ? 'transparent' : bg, border: dashed ? `1px dashed ${fg}` : 'none', height: 22, ...sx }} />;
};

export const ClubChips: React.FC<{ clubs: string[] }> = ({ clubs }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
    {clubs.length ? clubs.map((c) => (
      <Chip key={c} size="small" label={c} sx={{ height: 20, fontSize: 11, bgcolor: '#fff6cc', color: '#6b5200', letterSpacing: '.02em' }} />
    )) : <Typography sx={{ fontSize: 12, color: C.warn }}>클럽 없음</Typography>}
  </Box>
);

/** 발급률 도넛 */
export const Donut: React.FC<{ pct: number; size?: number; label?: string }> = ({ pct, size = 128, label = '발급률' }) => {
  const r = 46; const c = 2 * Math.PI * r; const v = Math.max(0, Math.min(100, pct));
  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg viewBox="0 0 110 110" width={size} height={size}>
        <defs>
          <linearGradient id="btGold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffe066" /><stop offset="100%" stopColor="#d99a00" />
          </linearGradient>
        </defs>
        <circle cx="55" cy="55" r={r} fill="none" stroke="#f4eed6" strokeWidth="11" />
        <circle cx="55" cy="55" r={r} fill="none" stroke="url(#btGold)" strokeWidth="11" strokeLinecap="round"
          strokeDasharray={`${(c * v) / 100} ${c}`} transform="rotate(-90 55 55)" />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{v.toFixed(v % 1 ? 1 : 0)}%</Typography>
          <Typography sx={{ fontSize: 11, color: C.sub }}>{label}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

/** 얇은 금색 그라데이션 막대 + % 알약 */
export const GoldBar: React.FC<{ label: string; done: number; total: number }> = ({ label, done, total }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr 54px', alignItems: 'center', gap: 1.25, py: 0.75 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.03em' }}>{label}</Typography>
      <Box>
        <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f4eed6', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: 'linear-gradient(90deg,#ffe066,#f5b800 60%,#d99a00)' }} />
        </Box>
        <Typography sx={{ fontSize: 11, color: C.sub, mt: 0.25 }}>{done} / {total}명</Typography>
      </Box>
      <Box sx={{ justifySelf: 'end', px: 1, py: 0.1, borderRadius: 10, bgcolor: '#fff3cf', color: '#8a5d00', fontSize: 12, fontWeight: 800 }}>{pct}%</Box>
    </Box>
  );
};

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ py: 6, textAlign: 'center', color: C.sub, fontSize: 14 }}>{children}</Box>
);
